import { useEffect, useState } from 'react'
import Button from '../components/ui/Button'
import LoadingState from '../components/ui/LoadingState'
import PageHeader from '../components/ui/PageHeader'
import PageShell from '../components/ui/PageShell'
import { formatDateTime } from '../utils/date'
import { apiClient } from '../services/api'
import { ui } from '../styles/ui'
import { useTranslation } from '../contexts/TranslationContext'
import { useAuthStore } from '../services/authStore'
import {
  clearActiveAutoTranslationJob,
  fetchAutoTranslationJob,
  getActiveAutoTranslationJob,
  fetchI18nSyncStatus,
  runAutoTranslation,
  setActiveAutoTranslationJob,
  syncI18nWithCode,
} from '../services/translationService'
import useNotificationStore from '../services/notificationStore'

const AUTO_SCOPES = [
  { key: 'ui_translations', labelKey: 'UI translations' },
  { key: 'product_categories', labelKey: 'Product categories' },
  { key: 'receipt_categories', labelKey: 'Receipt categories' },
]

const resolveAutoScopeLabel = (t, scope) => {
  switch (scope.key) {
    case 'ui_translations':
      return t('translations.scope.ui_translations', 'UI translations')
    case 'product_categories':
      return t('translations.scope.product_categories', 'Product categories')
    case 'receipt_categories':
      return t('translations.scope.receipt_categories', 'Receipt categories')
    default:
      return scope.labelKey
  }
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))
const AUTO_TRANSLATION_POLL_INTERVAL_MS = 1000
const AUTO_TRANSLATION_POLL_TIMEOUT_MS = 15 * 60 * 1000

const SYNC_STATS_CONFIG = [
  { key: 'created', label: 'Created' },
  { key: 'updated', label: 'Updated' },
  { key: 'unchanged', label: 'Unchanged' },
  { key: 'missing', label: 'Missing' },
  { key: 'locales', label: 'Locales' },
  { key: 'files', label: 'Files' },
]

const formatDuration = (durationMs) => {
  const value = Number(durationMs)
  if (!Number.isFinite(value) || value < 0) return null
  return value >= 1000 ? `${(value / 1000).toFixed(1)}s` : `${Math.round(value)}ms`
}

const formatSyncStats = (stats, maxItems = 6) => {
  if (!stats || typeof stats !== 'object') return ''

  const segments = []
  for (const entry of SYNC_STATS_CONFIG) {
    const value = stats[entry.key]
    if (value == null || value === '') continue
    const num = Number(value)
    if (!Number.isFinite(num)) continue
    segments.push(`${entry.label}: ${num}`)
    if (segments.length >= maxItems) break
  }

  if (segments.length < maxItems) {
    const duration = formatDuration(stats.durationMs)
    if (duration) {
      segments.push(`Duration: ${duration}`)
    }
  }

  return segments.join(' · ')
}

const stripTodoPrefix = (value, fallback = '') => {
  const text = String(value || fallback || '').trim()
  if (!text) return ''
  return text.replace(/^TODO:\s*/i, '')
}

const toOptionalNumber = (value) => {
  if (value == null || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

export default function Translations() {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const isAdmin = Boolean(user?.is_admin ?? user?.isAdmin)
  const [locale, setLocale] = useState('en')
  const [translations, setTranslations] = useState([])
  const [languages, setLanguages] = useState([])
  const [syncStatus, setSyncStatus] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [search, setSearch] = useState('')
  const [syncing, setSyncing] = useState(false)
  const [autoSourceLanguage, setAutoSourceLanguage] = useState('en')
  const [autoTargetLanguages, setAutoTargetLanguages] = useState([])
  const [autoScopes, setAutoScopes] = useState(['ui_translations'])
  const [autoMode, setAutoMode] = useState('missing_only')
  const [autoDryRun, setAutoDryRun] = useState(false)
  const [autoOverwrite, setAutoOverwrite] = useState(false)
  const [autoRunning, setAutoRunning] = useState(false)
  const [autoResult, setAutoResult] = useState(null)
  const [autoError, setAutoError] = useState('')
  const upsertOperation = useNotificationStore((state) => state.upsertOperation)
  const showToast = useNotificationStore((state) => state.showToast)
  const dismissToast = useNotificationStore((state) => state.dismissToast)
  const addOrUpdateNotification = useNotificationStore((state) => state.addOrUpdateNotification)

  const fetchLanguages = async () => {
    const response = await apiClient.get('/languages')
    const langs = Array.isArray(response.data?.data) ? response.data.data : []
    setLanguages(langs)
  }

  const fetchTranslations = async (loc, searchQuery = '') => {
    const params = { locale: loc, page: 1, per_page: 200 }
    if (searchQuery) params.search = searchQuery
    const response = await apiClient.get('/translations', { params })
    setTranslations(response.data.translations || [])
  }

  const fetchStatus = async () => {
    try {
      const status = await fetchI18nSyncStatus()
      setSyncStatus(status)
    } catch (_err) {
      setSyncStatus(null)
    }
  }

  const loadAll = async () => {
    setLoading(true)
    setError('')
    try {
      await fetchLanguages()
      await fetchTranslations(locale, search)
      await fetchStatus()
    } catch (_err) {
      setError(t('Could not load translations', 'Could not load translations'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadAll()
  }, [])

  useEffect(() => {
    if (!locale) return
    fetchTranslations(locale, search).catch(() => setError(t('Could not load translations', 'Could not load translations')))
  }, [locale, search])

  useEffect(() => {
    if (!languages.length) return
    if (!languages.some((item) => item.code === autoSourceLanguage)) {
      const fallback = languages.find((item) => item.active)?.code || languages[0]?.code || 'en'
      setAutoSourceLanguage(fallback)
    }
  }, [languages, autoSourceLanguage])

  useEffect(() => {
    setAutoTargetLanguages((prev) => prev.filter((code) => code !== autoSourceLanguage))
  }, [autoSourceLanguage])

  const handleSyncWithCode = async () => {
    if (syncing) return
    setSyncing(true)
    setError('')
    setMessage('')
    try {
      const result = await syncI18nWithCode()
      const statsMessage = formatSyncStats(result?.stats)
      const summaryMessage = [result?.message, statsMessage].filter(Boolean).join('. ')

      await loadAll()
      setMessage(result?.message || t('Sync with code completed', 'Sync with code completed'))
      showToast({
        id: `i18n-sync-success-${Date.now()}`,
        kind: 'notification',
        severity: 'success',
        title: t('Translations synced with code', 'Translations synced with code'),
        message: summaryMessage || t('Sync with code completed', 'Sync with code completed'),
      })
    } catch (err) {
      const statsMessage = formatSyncStats(err?.stats)
      const normalizedMessage = err?.message || t('Sync with code failed', 'Sync with code failed')
      const summaryMessage = [normalizedMessage, statsMessage].filter(Boolean).join('. ')

      setError(normalizedMessage)
      showToast({
        id: `i18n-sync-failed-${Date.now()}`,
        kind: 'notification',
        severity: 'error',
        title: t('Translation sync failed', 'Translation sync failed'),
        message: summaryMessage,
        persistent: true,
      })
    } finally {
      setSyncing(false)
    }
  }

  const canSync = Boolean(isAdmin && syncStatus?.admin_sync_action_enabled)
  const coverageRows = Array.isArray(syncStatus?.translation_file_coverage) ? syncStatus.translation_file_coverage : []
  const warnings = Array.isArray(syncStatus?.warnings) ? syncStatus.warnings : []
  const lastSyncValue = syncStatus?.last_synced_timestamp
    ? formatDateTime(syncStatus.last_synced_timestamp)
    : t('Never', 'Never')

  const operationProgressToastId = (jobId) => `operation-progress-${jobId}`

  const markAutoTranslationRunning = (jobId, statusPayload) => {
    const status = typeof statusPayload === 'string' ? statusPayload : String(statusPayload?.status || '')
    const operation = statusPayload && typeof statusPayload === 'object'
      ? statusPayload.operation || null
      : null
    const progressCurrent = toOptionalNumber(operation?.progressCurrent)
    const progressTotal = toOptionalNumber(operation?.progressTotal)
    const title = stripTodoPrefix(
      operation?.title,
      t('Auto-translation in progress', 'Auto-translation in progress'),
    )
    const message = stripTodoPrefix(
      operation?.message,
      t('Background translation job is running.', 'Background translation job is running.'),
    )

    upsertOperation({
      id: jobId,
      type: 'auto_translation',
      status: status === 'queued' ? 'queued' : 'running',
      progressCurrent,
      progressTotal,
    })
    showToast({
      id: operationProgressToastId(jobId),
      kind: 'operation-progress',
      severity: 'info',
      title,
      message,
      progressCurrent,
      progressTotal,
      operationId: jobId,
      persistent: true,
    })
  }

  const markAutoTranslationCompleted = (jobId, result) => {
    dismissToast(operationProgressToastId(jobId))
    upsertOperation({
      id: jobId,
      type: 'auto_translation',
      status: 'completed',
      progressCurrent: null,
      progressTotal: null,
    })
    addOrUpdateNotification({
      id: `i18n-auto-${jobId}`,
      type: 'i18n.auto_translation.completed',
      channel: 'i18n',
      severity: 'success',
      title: t('Translations synced', 'Translations synced'),
      message: t('Auto-translation job completed successfully.', 'Auto-translation job completed successfully.'),
      actionUrl: '/admin/translations',
      status: 'unread',
      createdAt: new Date().toISOString(),
      payload: result,
    })
    showToast({
      id: `i18n-auto-success-${jobId}`,
      kind: 'notification',
      severity: 'success',
      title: t('Translations synced', 'Translations synced'),
      message: t('Auto-translation job completed successfully.', 'Auto-translation job completed successfully.'),
    })
  }

  const markAutoTranslationFailed = (jobId, errorMessage) => {
    dismissToast(operationProgressToastId(jobId))
    upsertOperation({
      id: jobId,
      type: 'auto_translation',
      status: 'failed',
      progressCurrent: null,
      progressTotal: null,
    })
    addOrUpdateNotification({
      id: `i18n-auto-${jobId}`,
      type: 'i18n.auto_translation.failed',
      channel: 'i18n',
      severity: 'error',
      title: t('Auto-translation failed', 'Auto-translation failed'),
      message: errorMessage,
      actionUrl: '/admin/translations',
      status: 'unread',
      createdAt: new Date().toISOString(),
    })
    showToast({
      id: `i18n-auto-failed-${jobId}`,
      kind: 'notification',
      severity: 'error',
      title: t('Auto-translation failed', 'Auto-translation failed'),
      message: errorMessage,
      persistent: true,
    })
  }

  const pollAutoTranslationJob = async (jobId) => {
    const startedAt = Date.now()
    while (Date.now() - startedAt < AUTO_TRANSLATION_POLL_TIMEOUT_MS) {
      const status = await fetchAutoTranslationJob(jobId)
      if (status.status === 'queued' || status.status === 'processing') {
        markAutoTranslationRunning(jobId, status)
      }
      if (status.status === 'completed') {
        clearActiveAutoTranslationJob()
        markAutoTranslationCompleted(jobId, status.result)
        return status.result
      }
      if (status.status === 'failed') {
        clearActiveAutoTranslationJob()
        const errorMessage = status.error || t('Auto-translation job failed', 'Auto-translation job failed')
        markAutoTranslationFailed(jobId, errorMessage)
        throw new Error(errorMessage)
      }
      await sleep(AUTO_TRANSLATION_POLL_INTERVAL_MS)
    }
    throw new Error(t('Auto-translation job timed out', 'Auto-translation job timed out'))
  }

  useEffect(() => {
    let active = true

    const resumePendingAutoTranslation = async () => {
      const pending = getActiveAutoTranslationJob()
      if (!pending?.jobId) return

      setAutoRunning(true)
      setAutoError('')
      markAutoTranslationRunning(pending.jobId, { status: 'processing' })

      try {
        const result = await pollAutoTranslationJob(pending.jobId)
        if (!active) return
        setAutoResult(result)
        await Promise.all([
          fetchTranslations(locale, search),
          fetchStatus(),
        ])
      } catch (err) {
        if (!active) return
        const statusCode = Number(err?.response?.status || 0)
        if (statusCode === 404) {
          clearActiveAutoTranslationJob()
        }
        setAutoError(err?.response?.data?.message || err?.message || t('Auto-translation failed', 'Auto-translation failed'))
      } finally {
        if (active) {
          setAutoRunning(false)
        }
      }
    }

    resumePendingAutoTranslation()

    return () => {
      active = false
    }
  }, [])

  const handleAutoTargetToggle = (code) => {
    setAutoTargetLanguages((prev) => (
      prev.includes(code) ? prev.filter((item) => item !== code) : [...prev, code]
    ))
  }

  const handleAutoScopeToggle = (scopeKey) => {
    setAutoScopes((prev) => (
      prev.includes(scopeKey) ? prev.filter((item) => item !== scopeKey) : [...prev, scopeKey]
    ))
  }

  const handleRunAutoTranslation = async () => {
    setAutoError('')
    setAutoResult(null)

    if (autoTargetLanguages.length === 0) {
      setAutoError(t('Select at least one target language', 'Select at least one target language'))
      return
    }
    if (autoScopes.length === 0) {
      setAutoError(t('Select at least one scope', 'Select at least one scope'))
      return
    }
    if (autoMode === 'retranslate_selected' && !autoOverwrite) {
      setAutoError(t('Overwrite must be enabled for re-translate mode', 'Overwrite must be enabled for re-translate mode'))
      return
    }

    setAutoRunning(true)
    try {
      const queued = await runAutoTranslation({
        sourceLanguage: autoSourceLanguage,
        targetLanguages: autoTargetLanguages,
        scopes: autoScopes,
        mode: autoMode,
        overwrite: autoOverwrite,
        dryRun: autoDryRun,
      })
      markAutoTranslationRunning(queued.jobId, 'queued')
      setActiveAutoTranslationJob({
        jobId: queued.jobId,
        startedAt: new Date().toISOString(),
      })
      const result = await pollAutoTranslationJob(queued.jobId)
      setAutoResult(result)
      await Promise.all([
        fetchTranslations(locale, search),
        fetchStatus(),
      ])
    } catch (err) {
      setAutoError(err?.response?.data?.message || err?.message || t('Auto-translation failed', 'Auto-translation failed'))
    } finally {
      setAutoRunning(false)
    }
  }

  const autoRunnable = Boolean(
    isAdmin &&
    autoTargetLanguages.length > 0 &&
    autoScopes.length > 0 &&
    (autoMode !== 'retranslate_selected' || autoOverwrite),
  )

  const scopeRows = autoResult?.result

  return (
    <PageShell>
      <PageHeader
        title={t('translations.title', 'Translation Management')}
        subtitle={t('translations.file_backed_notice', 'Static translations are managed by code files (config/i18n/translations/*.json). Runtime editing is disabled.')}
      />
      {error && <div className={ui.alertError}>{error}</div>}
      {message && <div className={ui.alertInfo}>{message}</div>}

      <div className={ui.layout.contentGrid}>
        <div className={ui.cards.compact}><div className="text-muted-foreground">{t('Source', 'Source')}</div><div className="font-semibold">{t('Code files', 'Code files')}</div></div>
        <div className={ui.cards.compact}><div className="text-muted-foreground">{t('Last sync', 'Last sync')}</div><div className="font-semibold">{lastSyncValue}</div></div>
        <div className={ui.cards.compact}><div className="text-muted-foreground">{t('Canonical keys', 'Canonical keys')}</div><div className="font-semibold">{syncStatus?.canonical_key_count ?? t('n/a', 'n/a')}</div></div>
        <div className={ui.cards.compact}><div className="text-muted-foreground">{t('Files', 'Files')}</div><div className="font-semibold">{syncStatus?.translation_file_count ?? t('n/a', 'n/a')}</div></div>
      </div>

      <div className={`${ui.card} ${ui.cardBody}`}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
            <label className="font-semibold whitespace-nowrap">{t('translations.lang_code', 'Lang code')}</label>
            <select value={locale} onChange={(e) => setLocale(e.target.value)} className={ui.select}>
              {languages.filter((l) => l.active).map((loc) => (
                <option key={loc.code} value={loc.code}>{loc.code}</option>
              ))}
            </select>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`${ui.input} min-w-[220px]`}
              placeholder={t('translations.filter_placeholder', 'Filter by key or value')}
            />
          </div>
          <div>
            <Button
              onClick={handleSyncWithCode}
              disabled={!canSync || syncing}
            >
              {syncing ? t('Syncing...', 'Syncing...') : t('translations.sync_with_code', 'Sync with code')}
            </Button>
            {!canSync && <p className="mt-1 text-xs text-muted-foreground">{t('Sync action disabled or unavailable.', 'Sync action disabled or unavailable.')}</p>}
          </div>
        </div>
      </div>

      <div className={`${ui.card} ${ui.cardBody} mt-4`}>
        <h2 className="text-lg font-semibold mb-3">{t('Auto Translate', 'Auto Translate')}</h2>
        {autoError && <div className={`${ui.alertError} mb-3`}>{autoError}</div>}
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <label htmlFor="auto-source-language" className="block text-sm font-semibold mb-1">{t('Source language', 'Source language')}</label>
            <select id="auto-source-language" value={autoSourceLanguage} onChange={(e) => setAutoSourceLanguage(e.target.value)} className={ui.select}>
              {languages.filter((item) => item.active).map((item) => (
                <option key={item.code} value={item.code}>{item.code}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="auto-translation-mode" className="block text-sm font-semibold mb-1">{t('Mode', 'Mode')}</label>
            <select id="auto-translation-mode" value={autoMode} onChange={(e) => setAutoMode(e.target.value)} className={ui.select}>
              <option value="missing_only">{t('Missing keys only', 'Missing keys only')}</option>
              <option value="retranslate_selected">{t('Re-translate selected', 'Re-translate selected')}</option>
            </select>
          </div>
        </div>

        <div className="mt-3">
          <div className="text-sm font-semibold mb-1">{t('Target languages', 'Target languages')}</div>
          <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3">
            {languages.filter((item) => item.active && item.code !== autoSourceLanguage).map((item) => (
              <label key={item.code} className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={autoTargetLanguages.includes(item.code)}
                  onChange={() => handleAutoTargetToggle(item.code)}
                />
                <span>{item.code}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="mt-3">
          <div className="text-sm font-semibold mb-1">{t('Scopes', 'Scopes')}</div>
          <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3">
            {AUTO_SCOPES.map((scope) => (
              <label key={scope.key} className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={autoScopes.includes(scope.key)}
                  onChange={() => handleAutoScopeToggle(scope.key)}
                />
                <span>{resolveAutoScopeLabel(t, scope)}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="mt-3 grid gap-2 md:grid-cols-2">
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={autoDryRun} onChange={(e) => setAutoDryRun(e.target.checked)} />
            <span>{t('Dry run', 'Dry run')}</span>
          </label>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={autoOverwrite} onChange={(e) => setAutoOverwrite(e.target.checked)} />
            <span>{t('Overwrite existing translations', 'Overwrite existing translations')}</span>
          </label>
        </div>

        <div className="mt-3">
          <Button
            type="button"
            disabled={!autoRunnable || autoRunning}
            onClick={handleRunAutoTranslation}
          >
            {autoRunning ? t('Running...', 'Running...') : t('Run Auto Translate', 'Run Auto Translate')}
          </Button>
        </div>

        {scopeRows && (
          <div className="mt-4 rounded-lg border bg-surface p-3">
            <div className="font-semibold mb-2">{t('Result summary', 'Result summary')} {autoResult?.dryRun ? t('(dry-run)', '(dry-run)') : ''}</div>
            <div className="overflow-x-auto">
              <table className={ui.table}>
                <thead className={ui.tableHead}>
                  <tr>
                    <th className={ui.tableHeadCell}>{t('Scope', 'Scope')}</th>
                    <th className={ui.tableHeadCell}>{t('Missing', 'Missing')}</th>
                    <th className={ui.tableHeadCell}>{t('Translated', 'Translated')}</th>
                    <th className={ui.tableHeadCell}>{t('Failed', 'Failed')}</th>
                    <th className={ui.tableHeadCell}>{t('Skipped', 'Skipped')}</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(scopeRows).map(([scope, values]) => (
                    <tr key={scope}>
                      <td className={ui.tableCellText}>{scope}</td>
                      <td className={ui.tableCellText}>{values?.missing ?? 0}</td>
                      <td className={ui.tableCellText}>{values?.translated ?? 0}</td>
                      <td className={ui.tableCellText}>{values?.failed ?? 0}</td>
                      <td className={ui.tableCellText}>{values?.skipped ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {warnings.length > 0 && (
        <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <div className="font-semibold mb-1">{t('Validation warnings', 'Validation warnings')}</div>
          <ul className="list-disc pl-5">
            {warnings.map((warning, idx) => <li key={`${warning}-${idx}`}>{warning}</li>)}
          </ul>
        </div>
      )}

      {coverageRows.length > 0 && (
        <div className="mt-4 overflow-x-auto rounded-lg border bg-surface">
          <table className={`${ui.table}`}>
            <thead className={ui.tableHead}>
              <tr>
                <th className={ui.tableHeadCell}>{t('Language', 'Language')}</th>
                <th className={ui.tableHeadCell}>{t('Key count', 'Key count')}</th>
                <th className={ui.tableHeadCell}>{t('Missing keys', 'Missing keys')}</th>
              </tr>
            </thead>
            <tbody>
              {coverageRows.map((row) => (
                <tr key={row.code}>
                  <td className={ui.tableCellText}>{row.code}</td>
                  <td className={ui.tableCellText}>{row.key_count}</td>
                  <td className={ui.tableCellText}>{row.missing_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {loading ? (
        <LoadingState text={t('Loading translations...', 'Loading translations...')} />
      ) : (
        <div className="overflow-x-auto mt-4">
          <table className={`${ui.table} bg-surface border rounded`}>
            <thead className={ui.tableHead}>
              <tr>
                <th className={ui.tableHeadCell}>{t('translations.key', 'Key')}</th>
                <th className={ui.tableHeadCell}>{t('translations.value', 'Value')}</th>
              </tr>
            </thead>
            <tbody>
              {translations.map((item) => (
                <tr key={item.key}>
                  <td className={ui.tableCellText}>{item.key}</td>
                  <td className={ui.tableCellText}>{item.value || '-'}</td>
                </tr>
              ))}
              {translations.length === 0 && (
                <tr>
                  <td colSpan={2} className="text-center p-3 text-muted-foreground">{t('translations.no_records', 'No translations yet')}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </PageShell>
  )
}



